import {
  useCreateServiceTicketTaskMutation,
  useUpdateServiceTicketTaskMutation,
} from '@/store/api/services/operations/serviceTickets/serviceTickets';
import { useGetTaskTypesQuery } from '@/store/api/services/operations/taskTypes';
import type { ServiceTicketTask } from '@/types/serviceTicket';
import { useModal } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

interface ManageServiceTicketTaskProps extends ModalChildrenProps {
  props: {
    serviceTicketId: string;
    /** When present the modal edits an existing task; otherwise it adds a new one. */
    task?: ServiceTicketTask;
  } | null;
}

const ManageServiceTicketTaskSchema = z.object({
  task_type_id: z.string().min(1, 'Task type is required'),
  description: z.string().optional(),
});

type ManageServiceTicketTaskSchemaType = z.infer<typeof ManageServiceTicketTaskSchema>;

/**
 * Adds or edits a single task on a service ticket. Acts as "add" when no task is
 * supplied via props and "edit" when an existing task is passed in.
 */
const ManageServiceTicketTask = ({ hide, props }: ManageServiceTicketTaskProps) => {
  const serviceTicketId = props?.serviceTicketId;
  const task = props?.task;
  const isEditing = !!task;

  const { show } = useModal();

  const { data: taskTypesResponse } = useGetTaskTypesQuery({
    pagination: {
      current_page: 1,
      items_per_page: 1000,
      sort_by: 'name',
      sort_ascending: true,
    },
  });
  const taskTypes = taskTypesResponse?.data || [];

  const [createTask] = useCreateServiceTicketTaskMutation();
  const [updateTask] = useUpdateServiceTicketTaskMutation();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ManageServiceTicketTaskSchemaType>({
    resolver: zodResolver(ManageServiceTicketTaskSchema),
    mode: 'onSubmit',
    defaultValues: {
      task_type_id: task?.task_type_id || '',
      description: task?.description || '',
    },
  });

  const onSubmit = async (data: ManageServiceTicketTaskSchemaType) => {
    if (!serviceTicketId) {
      toast.error('Service ticket is missing');
      return;
    }

    try {
      if (isEditing && task) {
        await updateTask({
          serviceTicketId,
          taskId: task.id,
          task_type_id: data.task_type_id,
          description: data.description,
          sort_order: task.sort_order,
        }).unwrap();
        toast.success('Task updated successfully');
      } else {
        await createTask({
          serviceTicketId,
          task_type_id: data.task_type_id,
          description: data.description,
        }).unwrap();
        toast.success('Task assigned successfully');
      }
      hide();
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message ||
        error?.data?.message ||
        error?.message ||
        `Failed to ${isEditing ? 'update' : 'assign'} task`;
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          <Stack gap={0.5}>
            <Controller
              name="task_type_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors.task_type_id} required>
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    {...field}
                    label="Task Type"
                    onChange={(e) => {
                      const taskTypeId = e.target.value;
                      field.onChange(taskTypeId);
                      // Prefill description from the task type's own description (only
                      // when adding and the field is still empty, so we never clobber
                      // text the user already typed or an existing task's description).
                      if (!isEditing) {
                        const selectedType = taskTypes.find((type) => type.id === taskTypeId);
                        if (selectedType?.description) {
                          setValue('description', selectedType.description);
                        }
                      }
                    }}
                  >
                    {taskTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            {errors.task_type_id && (
              <Typography variant="caption" color="error">
                {errors.task_type_id.message}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => show('create-task-type')}
            >
              + Create new task type
            </Typography>
          </Stack>

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Description
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextareaAutosize
                  {...field}
                  minRows={3}
                  maxRows={6}
                  placeholder="Describe the task details..."
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    padding: '12px 14px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    lineHeight: '1.5',
                    backgroundColor: 'transparent',
                    outline: 'none',
                    cursor: 'text',
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide()} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting
            ? isEditing
              ? 'Saving...'
              : 'Assigning...'
            : isEditing
              ? 'Save Changes'
              : 'Assign Task'}
        </Button>
      </DialogActions>
    </>
  );
};

export default ManageServiceTicketTask;
